import { useAppStore } from '@core/store'
import { ApiError } from '@core/api/client'
import { esperaMs } from '@viewer/room/roomClient'
import type { ClashResult } from '@viewer/clash/lib'
import { clashKey } from '@viewer/clash/lib'
import { origemDaAlteracao } from './clashAlteracaoOrigem'
import { lerClassificacao, type ClashClassificacaoFile } from './clashAgrupamentoEstado'
import type { ClashGuidMap } from './clashBcf'
import { camposDoSetup, setupDoEstado, type ClashSlice } from './store/clashSlice'
import { chaveDosGuids, type ClashCorrida } from './clashReconciliacao'
import { temParecer, type ClashTriageMap } from './clashTriage'
import {
  loadSetup,
  persistTudo,
  sanitizeCorridas,
  sanitizeSetup,
  type ClashSetupFile,
  type KeyValueStore,
} from './clashTriageStorage'
import {
  CAMPOS_CLASSIFICACAO,
  CAMPOS_SETUP,
  CONFIG_CLASSIFICACAO,
  CONFIG_PROJECTO,
  CONFIG_SETUP,
  camposDaEntrada,
  camposEfectivos,
  corridasNovas,
  diferencasDaTriagem,
  diferencasDoObjecto,
  diferencasDosGrupos,
  entradaDosCampos,
  filaParaEnvio,
  guidsDaIdentidade,
  idDoItem,
  identidadeDaChave,
  iguais,
  indiceDeIdentidades,
  juntarAFila,
  juntarAoEspelho,
  normalizar,
  pendentesNaFila,
  rotuloDoItem,
  textoDoAvisoEnviado,
  textoDoAvisoRecebido,
  tirarDaFila,
  tirarItemDaFila,
  ultimoCarimbo,
  type AlteracaoParaEnvio,
  type AvisoDoServidor,
  type Espelho,
  type EstadoDaLigacao,
  type Fila,
  type ItemPartilhado,
} from './clashPartilha'
import { portaHttp, type PortaDaPartilha } from './clashPartilhaPorta'
import type { AbrirCanal, CanalDaPartilha, Presente } from './clashPartilhaCanal'
import { registarGanchoDaPartilha, type ParDaCorrida } from './clashPartilhaGancho'

// ---------------------------------------------------------------------------
// O motor da triagem partilhada: local primeiro, servidor depois.
//
// A triagem continua a viver no store e no localStorage, como sempre. O
// motor observa o store e, do que muda, tira as alteracoes campo a campo
// (por identidade de GlobalIds) para uma FILA guardada no localStorage.
// A fila sai em lote, um segundo depois da ultima alteracao (tres no
// maximo, para quem nao para de escrever). Sem rede, a fila fica e o
// trabalho continua; ao voltar a rede, sai.
//
// Do servidor vem "o que mudou desde a versao N", acordado pelo canal ao
// vivo (ou de 20 em 20 s sem canal). O que chega vai para o ESPELHO (o
// que o servidor tem) e dai para a triagem local, com uma regra: o que
// esta na fila ganha no ecra ate o servidor decidir. A resposta ao envio
// traz o estado actual dos campos em que a alteracao daqui perdeu.
//
// O que NAO se envia: as mudancas que nao sao decisoes (ler o guardado,
// repor uma sessao: ver clashAlteracaoOrigem.ts) nao apagam nada no
// servidor; uma sessao reposta leva os seus pareceres com as datas que
// tinham, e perde para o que um colega decidiu depois.
// ---------------------------------------------------------------------------

/** Por quanto tempo se juntam as alteracoes antes de enviar. */
export const ESPERA_ENVIO_MS = 1000
/** Mesmo a escrever sem parar, envia-se pelo menos isto. */
export const ESPERA_MAXIMA_MS = 3000
/** Sem canal ao vivo, pergunta-se ao servidor de tanto em tanto. */
export const SONDAGEM_MS = 20_000
/** O "estou a editar" renova-se antes de expirar no servidor (60 s). */
export const RENOVAR_EDICAO_MS = 30_000
/** Alteracoes por pedido (o servidor aceita 2000). */
export const LOTE = 500

/** Momento das alteracoes de configuracao que vem de uma substituicao:
 *  so preenchem o que o servidor nao tem. */
const MOMENTO_ZERO = '1970-01-01T00:00:00.000Z'

export interface Relogio {
  agora: () => string
  agendar: (fn: () => void, ms: number) => () => void
}

const relogioReal: Relogio = {
  agora: () => new Date().toISOString(),
  agendar: (fn, ms) => {
    const t = window.setTimeout(fn, ms)
    return () => window.clearTimeout(t)
  },
}

export interface OpcoesDoMotor {
  projetoId: string
  codigo: string
  eu: { id: string; nome: string }
  porta?: PortaDaPartilha
  /** null: sem canal ao vivo (so sondagem). */
  abrirCanal?: AbrirCanal | null
  armazem?: KeyValueStore | null
  relogio?: Relogio
  /** Resolve os GlobalIds dos conflitos de uma corrida. */
  resolverGuids?: (r: readonly ClashResult[]) => Promise<ClashGuidMap>
  aoAviso?: (texto: string) => void
  /** O servidor deixou de aceitar (triagem arquivada, sem acesso). */
  aoParar?: (motivo: string) => void
  /** A triagem local foi limpa ("Fechar sessao"). */
  aoLimpar?: () => void
  /** Uma corrida feita neste browser foi registada. */
  aoCorrida?: (c: ClashCorrida) => void
  aoEstado?: (e: EstadoDoMotor) => void
  /** O store (por omissao, o da aplicacao). */
  loja?: LojaDosConflitos
  /** Onde a triagem local se grava (por omissao, o localStorage). */
  armazemDaTriagem?: KeyValueStore
}

export interface EstadoDoMotor {
  estado: EstadoDaLigacao
  pendentes: number
  presentes: Presente[]
  eu: number | null
  semCorrespondencia: number
  semIdentidade: number
  erro: string | null
}

type Estado = ClashSlice

/** O store dos conflitos (o da aplicacao; nos testes, um por browser). */
export interface LojaDosConflitos {
  getState: () => ClashSlice
  setState: (p: Partial<ClashSlice>) => void
  subscribe: (l: (s: ClashSlice, prev: ClashSlice) => void) => () => void
}

function armazemPorOmissao(): KeyValueStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

function setupDe(s: Estado): Record<string, unknown> {
  return setupDoEstado(s) as unknown as Record<string, unknown>
}

function classificacaoSemGrupos(c: ClashClassificacaoFile): Record<string, unknown> {
  return {
    regras: c.regras,
    especialidades: c.especialidades,
    agrupamento: c.agrupamento,
    exclusoes: c.exclusoes,
    classificadoEm: c.classificadoEm,
    agrupadoEm: c.agrupadoEm,
  }
}

export class MotorDaPartilha {
  readonly projetoId: string
  readonly codigo: string
  private readonly o: OpcoesDoMotor
  private readonly porta: PortaDaPartilha
  private readonly relogio: Relogio
  private readonly armazem: KeyValueStore | null
  private readonly loja: LojaDosConflitos

  private lido = 0
  private espelho: Espelho = {}
  private fila: Fila = {}
  /** Chave local -> identidade, dos resultados resolvidos. */
  private chaves: Record<string, string> = {}
  private semIdentidade = new Set<string>()

  private aplicandoRemoto = false
  private reconciliando = 0
  private adiado = false
  private parado = true
  private lidoUmaVez = false
  private semRede = false
  private falhas = 0
  private erro: string | null = null

  private enviando: Promise<void> | null = null
  private deNovo = false
  private cancelarEnvio: (() => void) | null = null
  private primeiroPendente: number | null = null
  private cancelarSondagem: (() => void) | null = null
  private cancelarRetoma: (() => void) | null = null
  private cancelarRenovacao: (() => void) | null = null

  private canal: CanalDaPartilha | null = null
  private ligado = false
  private canalRecusado = false
  private presentes: Presente[] = []
  private euNoCanal: number | null = null
  private aEditar: string | null = null

  private desligarStore: (() => void) | null = null

  constructor(o: OpcoesDoMotor) {
    this.o = o
    this.projetoId = o.projetoId
    this.codigo = o.codigo
    this.porta = o.porta ?? portaHttp
    this.relogio = o.relogio ?? relogioReal
    this.armazem = o.armazem === undefined ? armazemPorOmissao() : o.armazem
    this.loja = o.loja ?? useAppStore
  }

  // --- Ciclo de vida -----------------------------------------------------------

  iniciar(): void {
    if (!this.parado) return
    this.parado = false
    this.carregar()
    registarGanchoDaPartilha({
      inicio: () => {
        this.reconciliando += 1
      },
      fundir: (triagem, pares) => this.fundirPares(triagem, pares),
      fim: () => {
        this.reconciliando = Math.max(0, this.reconciliando - 1)
        if (this.reconciliando === 0 && this.adiado) {
          this.adiado = false
          this.aplicarNoStore(Object.values(this.espelho))
        }
      },
    })
    this.desligarStore = this.loja.subscribe((s, prev) => this.aoMudar(s, prev))
    if (this.o.abrirCanal) {
      this.canal = this.o.abrirCanal(this.projetoId, {
        aoVersao: (v, _porId, por, avisos) => this.aoVersao(v, por, avisos),
        aoPresenca: (p, eu) => {
          this.presentes = p
          this.euNoCanal = eu
          this.publicar()
        },
        aoEstado: (ligado) => {
          const voltou = ligado && !this.ligado
          this.ligado = ligado
          if (voltou) void this.sincronizar()
          this.agendarSondagem()
          this.publicar()
        },
        aoRecusar: () => {
          this.canalRecusado = true
          this.agendarSondagem()
          this.publicar()
        },
      })
    } else {
      this.agendarSondagem()
    }
    // O que estiver em memoria passa ja a reflectir o que se sabe.
    this.aplicarNoStore(Object.values(this.espelho))
    this.publicar()
    void this.sincronizar()
    void this.casarResultados()
  }

  parar(): void {
    if (this.parado) return
    this.parado = true
    this.desligarStore?.()
    this.desligarStore = null
    registarGanchoDaPartilha(null)
    this.canal?.fechar()
    this.canal = null
    for (const c of [this.cancelarEnvio, this.cancelarSondagem, this.cancelarRetoma, this.cancelarRenovacao]) c?.()
    this.cancelarEnvio = this.cancelarSondagem = this.cancelarRetoma = this.cancelarRenovacao = null
    this.gravar()
    this.publicar()
  }

  /** A rede voltou (evento `online`) ou o utilizador pediu. */
  retomar(): void {
    if (this.parado) return
    this.falhas = 0
    void this.sincronizar()
  }

  /** Envia tudo o que este browser tem (ao partilhar pela primeira vez). */
  carregarTudo(): void {
    const s = this.loja.getState()
    const agora = this.relogio.agora()
    const alteracoes: AlteracaoParaEnvio[] = []
    const d = diferencasDaTriagem({}, s.clashTriage, this.chaves, agora, false)
    for (const k of d.semIdentidade) this.semIdentidade.add(k)
    alteracoes.push(...d.alteracoes)
    alteracoes.push(...diferencasDosGrupos({}, s.clashClassificacao.grupos, agora, false))
    alteracoes.push(
      ...diferencasDoObjecto(CONFIG_CLASSIFICACAO, CAMPOS_CLASSIFICACAO, {}, classificacaoSemGrupos(s.clashClassificacao), agora),
    )
    if (s.clashSetupHydrated) {
      alteracoes.push(...diferencasDoObjecto(CONFIG_SETUP, CAMPOS_SETUP, {}, setupDe(s), agora))
    }
    alteracoes.push(...corridasNovas([], s.clashHistorico, agora))
    if (s.clashProjectoBcf) {
      alteracoes.push({ tipo: 'config', chave: CONFIG_PROJECTO, campos: { projectoBcf: s.clashProjectoBcf }, em: agora })
    }
    this.enfileirar(alteracoes)
    this.enviarJa()
  }

  /** O conflito que este browser esta a editar (bloqueio leve). */
  editar(key: string | null): void {
    const s = this.loja.getState()
    const id = key ? identidadeDaChave(key, s.clashTriage[key], this.chaves) : null
    if (id === this.aEditar) return
    this.aEditar = id
    this.canal?.editar(id)
    this.cancelarRenovacao?.()
    this.cancelarRenovacao = null
    if (id) this.agendarRenovacao()
  }

  /** A identidade de uma chave local (para a interface). */
  identidade(key: string | null): string | null {
    if (!key) return null
    return identidadeDaChave(key, this.loja.getState().clashTriage[key], this.chaves)
  }

  // --- Persistencia --------------------------------------------------------------

  private get chaveBase(): string {
    return `vn2r.clash.partilha.p.${this.projetoId}`
  }

  private carregar(): void {
    if (!this.armazem) return
    try {
      const raw = this.armazem.getItem(this.chaveBase)
      const o = raw ? (JSON.parse(raw) as Record<string, unknown>) : {}
      this.lido = typeof o.lido === 'number' ? o.lido : 0
      this.espelho = o.espelho && typeof o.espelho === 'object' ? (o.espelho as Espelho) : {}
      this.chaves = o.chaves && typeof o.chaves === 'object' ? (o.chaves as Record<string, string>) : {}
    } catch {
      this.lido = 0
      this.espelho = {}
    }
    try {
      const raw = this.armazem.getItem(`${this.chaveBase}.fila`)
      const f = raw ? (JSON.parse(raw) as Fila) : {}
      this.fila = f && typeof f === 'object' ? f : {}
    } catch {
      this.fila = {}
    }
  }

  /** A fila grava-se a parte e primeiro: e trabalho por enviar, e nao
   *  pode ficar de fora por o espelho ser grande para o localStorage. */
  private gravarFila(): void {
    try {
      this.armazem?.setItem(`${this.chaveBase}.fila`, JSON.stringify(this.fila))
    } catch {
      /* sem espaco: a fila fica em memoria */
    }
  }

  private gravar(): void {
    this.gravarFila()
    try {
      this.armazem?.setItem(
        this.chaveBase,
        JSON.stringify({ lido: this.lido, espelho: this.espelho, chaves: this.chaves }),
      )
    } catch {
      // Sem espaco para o espelho: na proxima abertura le-se tudo de novo
      // do servidor (desde 0). Nao se perde trabalho.
      try {
        this.armazem?.removeItem(this.chaveBase)
      } catch {
        /* nada a fazer */
      }
    }
  }

  // --- Do store para a fila ------------------------------------------------------

  private aoMudar(s: Estado, prev: Estado): void {
    if (this.parado || this.aplicandoRemoto) return
    if (prev.clashTriageHydrated && !s.clashTriageHydrated) {
      // "Fechar sessao": lousa limpa. Nada se apaga no servidor.
      this.o.aoLimpar?.()
      return
    }
    if (!s.clashTriageHydrated) return
    const origem = origemDaAlteracao()
    const apagar = origem === 'edicao'
    const agora = this.relogio.agora()
    const emConfig = origem === 'substituicao' ? MOMENTO_ZERO : agora
    const alteracoes: AlteracaoParaEnvio[] = []

    if (s.clashTriage !== prev.clashTriage) {
      const d = diferencasDaTriagem(prev.clashTriage, s.clashTriage, this.chaves, agora, apagar)
      for (const k of d.semIdentidade) if (s.clashTriage[k]) this.semIdentidade.add(k)
      alteracoes.push(...d.alteracoes)
    }
    if (s.clashClassificacao !== prev.clashClassificacao) {
      alteracoes.push(
        ...diferencasDosGrupos(prev.clashClassificacao.grupos, s.clashClassificacao.grupos, emConfig, apagar),
      )
      alteracoes.push(
        ...diferencasDoObjecto(
          CONFIG_CLASSIFICACAO,
          CAMPOS_CLASSIFICACAO,
          classificacaoSemGrupos(prev.clashClassificacao),
          classificacaoSemGrupos(s.clashClassificacao),
          emConfig,
        ),
      )
    }
    if (s.clashSetupHydrated && prev.clashSetupHydrated) {
      alteracoes.push(...diferencasDoObjecto(CONFIG_SETUP, CAMPOS_SETUP, setupDe(prev), setupDe(s), emConfig))
    }
    const corridas = corridasNovas(prev.clashHistorico, s.clashHistorico, agora)
    alteracoes.push(...corridas)
    if (corridas.length > 0 && origem !== 'substituicao') {
      // Uma corrida feita aqui: os modelos dela passam a ser os da triagem
      // partilhada (uma revisao nova dos IFC).
      const ultima = s.clashHistorico.at(-1)
      if (ultima) this.o.aoCorrida?.(ultima)
    }
    if (s.clashProjectoBcf && s.clashProjectoBcf !== prev.clashProjectoBcf) {
      alteracoes.push({
        tipo: 'config',
        chave: CONFIG_PROJECTO,
        campos: { projectoBcf: s.clashProjectoBcf },
        em: emConfig,
      })
    }
    this.enfileirar(alteracoes)
    if (origem === 'substituicao') {
      // Depois de ler o guardado ou repor uma sessao, a triagem local
      // volta a mostrar o que o servidor tem (menos o que esta por
      // enviar, que espera pela resposta).
      queueMicrotask(() => {
        if (!this.parado) this.aplicarNoStore(Object.values(this.espelho))
      })
    }
  }

  /** Junta a fila o que difere do que o servidor ja tem. */
  private enfileirar(alteracoes: readonly AlteracaoParaEnvio[]): void {
    const uteis: AlteracaoParaEnvio[] = []
    for (const a of alteracoes) {
      const id = idDoItem(a.tipo, a.chave)
      const item = this.espelho[id]
      const pend = this.fila[id]
      const campos: Record<string, unknown> = {}
      for (const [c, v] of Object.entries(a.campos)) {
        const noServidor = normalizar(c, item?.campos[c])
        // Igual ao que o servidor tem e nada por enviar nesse campo: e o
        // eco de uma aplicacao remota, ou uma volta ao mesmo valor.
        if (!pend?.campos[c] && iguais(noServidor, normalizar(c, v))) continue
        campos[c] = v
      }
      if (Object.keys(campos).length > 0) uteis.push({ ...a, campos })
    }
    if (uteis.length === 0) {
      this.publicar()
      return
    }
    this.fila = juntarAFila(this.fila, uteis)
    this.gravarFila()
    this.agendarEnvio()
    this.publicar()
  }

  // --- Envio ---------------------------------------------------------------------------

  private agendarEnvio(): void {
    if (this.parado) return
    const agoraMs = Date.parse(this.relogio.agora())
    if (this.primeiroPendente === null) this.primeiroPendente = agoraMs
    const restante = Math.max(0, this.primeiroPendente + ESPERA_MAXIMA_MS - agoraMs)
    this.cancelarEnvio?.()
    this.cancelarEnvio = this.relogio.agendar(() => {
      this.cancelarEnvio = null
      void this.sincronizar()
    }, Math.min(ESPERA_ENVIO_MS, restante))
  }

  private enviarJa(): void {
    this.cancelarEnvio?.()
    this.cancelarEnvio = null
    void this.sincronizar()
  }

  /** Envia a fila e le o que mudou. Uma de cada vez. */
  sincronizar(): Promise<void> {
    if (this.parado) return Promise.resolve()
    if (this.enviando) {
      // Ja ha uma a correr: fica marcada UMA seguinte, que apanha tudo o
      // que entrou entretanto (dez pedidos durante um envio sao uma
      // sincronizacao a seguir, nao dez).
      this.deNovo = true
      return this.enviando
    }
    this.primeiroPendente = null
    // Esta sincronizacao leva tudo o que esta na fila: o envio agendado
    // ja nao e preciso.
    this.cancelarEnvio?.()
    this.cancelarEnvio = null
    const corrida = this.fazerSincronizacao().finally(() => {
      this.enviando = null
      if (this.deNovo && !this.parado) {
        this.deNovo = false
        void this.sincronizar()
      }
    })
    this.enviando = corrida
    return corrida
  }

  /** Ha uma sincronizacao a correr ou marcada? (para os testes) */
  ocupado(): boolean {
    return this.enviando !== null || this.deNovo || this.cancelarEnvio !== null
  }

  private async fazerSincronizacao(): Promise<void> {
    try {
      const lote = filaParaEnvio(this.fila)
      for (let i = 0; i < lote.length; i += LOTE) {
        const parte = lote.slice(i, i + LOTE)
        const r = await this.porta.enviar(this.projetoId, this.lido, parte)
        if (this.parado) return
        this.fila = tirarDaFila(this.fila, parte)
        for (const inv of r.invalidas) this.fila = tirarItemDaFila(this.fila, inv.tipo, inv.chave)
        this.gravarFila()
        if (r.actuais.length > 0) {
          this.espelho = juntarAoEspelho(this.espelho, r.actuais)
          this.aplicarNoStore(r.actuais)
        }
        this.avisar(r.avisos, (a, rotulo) => textoDoAvisoEnviado(a, rotulo))
      }
      const leitura = await this.porta.ler(this.projetoId, this.lido)
      if (this.parado) return
      this.espelho = juntarAoEspelho(this.espelho, leitura.itens)
      this.lido = Math.max(this.lido, leitura.versao)
      this.lidoUmaVez = true
      this.semRede = false
      this.falhas = 0
      this.erro = null
      this.cancelarRetoma?.()
      this.cancelarRetoma = null
      this.aplicarNoStore(leitura.itens)
      this.gravar()
      // O que entrou na fila durante o envio vai no proximo.
      if (pendentesNaFila(this.fila) > 0 && !this.cancelarEnvio && !this.deNovo) this.agendarEnvio()
    } catch (err) {
      if (this.parado) return
      if (err instanceof ApiError && (err.status === 404 || err.status === 403 || err.status === 401)) {
        this.erro =
          err.status === 404
            ? 'Esta triagem partilhada já não está disponível: foi arquivada, ou o acesso foi retirado. A cópia deste browser fica como está.'
            : err.message
        this.publicar()
        this.o.aoParar?.(this.erro)
        return
      }
      if (err instanceof ApiError && err.status >= 400 && err.status < 500 && err.status !== 408 && err.status !== 429) {
        // Recusa do pedido inteiro: repetir dava o mesmo. A fila fica
        // (o trabalho nao se perde) e o motivo aparece na barra.
        this.erro = err.message
        return
      }
      // Sem rede ou servidor em baixo: a fila fica, volta-se a tentar.
      this.semRede = true
      this.falhas += 1
      this.cancelarRetoma?.()
      this.cancelarRetoma = this.relogio.agendar(() => {
        this.cancelarRetoma = null
        void this.sincronizar()
      }, esperaMs(this.falhas))
    } finally {
      this.publicar()
    }
  }

  private agendarSondagem(): void {
    this.cancelarSondagem?.()
    this.cancelarSondagem = null
    if (this.parado || (this.ligado && !this.canalRecusado)) return
    this.cancelarSondagem = this.relogio.agendar(() => {
      this.cancelarSondagem = null
      void this.sincronizar().finally(() => this.agendarSondagem())
    }, SONDAGEM_MS)
  }

  private agendarRenovacao(): void {
    this.cancelarRenovacao = this.relogio.agendar(() => {
      this.cancelarRenovacao = null
      if (this.parado || !this.aEditar) return
      this.canal?.editar(this.aEditar)
      this.agendarRenovacao()
    }, RENOVAR_EDICAO_MS)
  }

  private aoVersao(versao: number, por: string, avisos: AvisoDoServidor[]): void {
    // Os avisos em que eu perdi o valor (o colega escreveu depois).
    this.avisar(
      avisos.filter((a) => a.outro_id === this.o.eu.id && a.ficou === 'enviado'),
      (a, rotulo) => textoDoAvisoRecebido(a, por || 'Um colega', rotulo),
    )
    if (versao > this.lido) void this.sincronizar()
  }

  private avisar(avisos: readonly AvisoDoServidor[], texto: (a: AvisoDoServidor, rotulo: string) => string): void {
    if (avisos.length === 0 || !this.o.aoAviso) return
    const s = this.loja.getState()
    const indice = indiceDeIdentidades(s.clashTriage, this.chaves)
    const vistos = new Set<string>()
    for (const a of avisos.slice(0, 5)) {
      const k = `${a.tipo}:${a.chave}:${a.campo}`
      if (vistos.has(k)) continue
      vistos.add(k)
      this.o.aoAviso(texto(a, rotuloDoItem(a.tipo, a.chave, this.espelho, s.clashTriage, indice)))
    }
    if (avisos.length > 5) {
      this.o.aoAviso(`E mais ${avisos.length - 5} alterações em simultâneo com colegas.`)
    }
  }

  // --- Do servidor para o store -------------------------------------------------------

  /** Aplica itens do espelho a triagem local (com o que esta na fila por
   *  cima). Durante uma reconciliacao, espera pelo fim. */
  private aplicarNoStore(itens: readonly ItemPartilhado[]): void {
    if (this.parado || itens.length === 0) return
    if (this.reconciliando > 0) {
      this.adiado = true
      return
    }
    const s = this.loja.getState()
    if (!s.clashTriageHydrated) return
    let triagem = s.clashTriage
    let classificacao = s.clashClassificacao
    let setup: ClashSetupFile | null = null
    let historico = s.clashHistorico
    let projectoBcf = s.clashProjectoBcf
    const indice = indiceDeIdentidades(triagem, this.chaves)
    const agora = this.relogio.agora()

    for (const it0 of itens) {
      const id = idDoItem(it0.tipo, it0.chave)
      const it = this.espelho[id] ?? it0
      const campos = camposEfectivos(it, this.fila[id])
      if (it.tipo === 'conflito') {
        // A entrada que ja existe (normalmente uma); sem nenhuma, a chave
        // da ultima corrida deste browser com esses GlobalIds.
        const keys = indice.get(it.chave) ?? []
        const existentes = keys.filter((k) => k in triagem)
        for (const key of existentes.length > 0 ? existentes : keys.slice(0, 1)) {
          const base = triagem[key]
          const novo = entradaDosCampos(key, campos, base, guidsDaIdentidade(it.chave), ultimoCarimbo(it) ?? base?.updatedAt ?? agora)
          if (iguais(camposDaEntrada(novo), camposDaEntrada(base))) continue
          if (triagem === s.clashTriage) triagem = { ...triagem }
          if (novo) triagem[key] = novo
          else delete triagem[key]
        }
      } else if (it.tipo === 'grupo') {
        const lido = campos.titulo
          ? lerClassificacao({ grupos: { [it.chave]: campos } }).grupos[it.chave]
          : undefined
        const antes = classificacao.grupos[it.chave]
        if (iguais(lido ?? null, antes ?? null)) continue
        const grupos = { ...classificacao.grupos }
        if (lido) grupos[it.chave] = lido
        else delete grupos[it.chave]
        classificacao = { ...classificacao, grupos }
      } else if (it.tipo === 'config' && it.chave === CONFIG_CLASSIFICACAO) {
        const sobre: Record<string, unknown> = {}
        for (const c of CAMPOS_CLASSIFICACAO) if (campos[c] !== undefined && campos[c] !== null) sobre[c] = campos[c]
        const lida = lerClassificacao({ ...classificacaoSemGrupos(classificacao), ...sobre })
        const nova = { ...lida, grupos: classificacao.grupos }
        if (!iguais(classificacaoSemGrupos(nova), classificacaoSemGrupos(classificacao))) classificacao = nova
      } else if (it.tipo === 'config' && it.chave === CONFIG_SETUP) {
        if (!s.clashSetupHydrated) continue
        const actual = setup ?? (setupDe(s) as unknown as ClashSetupFile)
        const sobre: Record<string, unknown> = {}
        for (const c of CAMPOS_SETUP) if (campos[c] !== undefined && campos[c] !== null) sobre[c] = campos[c]
        const lido = sanitizeSetup({ ...actual, ...sobre })
        if (lido && !iguais(lido, actual)) setup = lido
      } else if (it.tipo === 'config' && it.chave === CONFIG_PROJECTO) {
        const p = campos.projectoBcf
        if (typeof p === 'string' && /^[0-9a-fA-F-]{36}$/.test(p) && p !== projectoBcf) projectoBcf = p
      } else if (it.tipo === 'corrida') {
        const c = campos.corrida as ClashCorrida | undefined
        if (!c || historico.some((h) => h.data === it.chave)) continue
        const lidas = sanitizeCorridas({ historico: [...historico, { ...c, data: it.chave }] }).historico
        historico = [...lidas].sort((a, b) => a.data.localeCompare(b.data)).slice(-50)
      }
    }

    const mudou =
      triagem !== s.clashTriage ||
      classificacao !== s.clashClassificacao ||
      setup !== null ||
      historico !== s.clashHistorico ||
      projectoBcf !== s.clashProjectoBcf
    if (mudou) this.escreverNoStore(triagem, classificacao, setup, historico, projectoBcf)
    this.publicar()
  }

  private escreverNoStore(
    triagem: ClashTriageMap,
    classificacao: ClashClassificacaoFile,
    setup: ClashSetupFile | null,
    historico: ClashCorrida[],
    projectoBcf: string | null,
  ): void {
    const s = this.loja.getState()
    const setupFinal: ClashSetupFile | null = setup ?? (s.clashSetupHydrated ? (setupDe(s) as unknown as ClashSetupFile) : loadSetup())
    this.aplicandoRemoto = true
    try {
      persistTudo(
        triagem,
        setupFinal,
        {
          historico,
          projectoBcf,
          chavesUltimaCorrida: s.clashChavesUltima,
          modulo: s.clashModulo,
          ultimaExportacao: s.clashUltimaExportacao,
        },
        this.o.armazemDaTriagem,
        classificacao,
      )
      this.loja.setState({
        clashTriage: triagem,
        clashClassificacao: classificacao,
        clashHistorico: historico,
        clashProjectoBcf: projectoBcf,
        ...(setup ? camposDoSetup(setup) : {}),
      })
    } finally {
      this.aplicandoRemoto = false
    }
  }

  // --- Corridas: casar pelos GlobalIds -----------------------------------------------

  /** Junta a triagem partilhada dos conflitos desta corrida (chamado pela
   *  reconciliacao, pelo gancho). Uma entrada local da mesma identidade
   *  com outra chave (outra revisao) passa para a chave desta corrida. */
  fundirPares(triagem: ClashTriageMap, pares: readonly ParDaCorrida[]): ClashTriageMap {
    const novas: Record<string, string> = {}
    for (const p of pares) {
      const [a, b] = p.guids
      if (a && b) novas[p.key] = chaveDosGuids([a, b], p.kind)
    }
    // As chaves que interessam: as desta corrida e as da triagem.
    const chaves: Record<string, string> = {}
    for (const [k, id] of Object.entries(this.chaves)) if (k in triagem) chaves[k] = id
    Object.assign(chaves, novas)
    this.chaves = chaves

    const indice = indiceDeIdentidades(triagem, chaves)
    let out = triagem
    const agora = this.relogio.agora()
    for (const [key, id] of Object.entries(novas)) {
      const it = this.espelho[idDoItem('conflito', id)]
      const pend = this.fila[idDoItem('conflito', id)]
      if (!it && !pend) continue
      const outras = (indice.get(id) ?? []).filter((k) => k !== key && k in out)
      const base = out[key] ?? (outras.length > 0 ? out[outras[0]] : undefined)
      const novo = entradaDosCampos(key, camposEfectivos(it, pend), base, guidsDaIdentidade(id), ultimoCarimbo(it) ?? base?.updatedAt ?? agora)
      if (out === triagem) out = { ...triagem }
      for (const k of outras) delete out[k]
      if (novo) out[key] = novo
      else delete out[key]
    }
    // As que nao tinham identidade e agora tem: vao inteiras.
    const agoraComId: AlteracaoParaEnvio[] = []
    for (const k of [...this.semIdentidade]) {
      if (!chaves[k]) continue
      this.semIdentidade.delete(k)
      const e = out[k]
      if (!e || !temParecer(e)) continue
      agoraComId.push(...diferencasDaTriagem({}, { [k]: e }, chaves, agora, false).alteracoes)
    }
    if (agoraComId.length > 0) this.enfileirar(agoraComId)
    this.gravar()
    this.publicar()
    return out
  }

  /** Resolve os GlobalIds dos resultados em memoria e casa-os com a
   *  triagem partilhada (ao entrar, ou depois de uma corrida cancelada). */
  async casarResultados(): Promise<void> {
    const r = this.loja.getState().clashResults
    if (this.parado || r.length === 0 || !this.o.resolverGuids) return
    let guids: ClashGuidMap
    try {
      guids = await this.o.resolverGuids(r)
    } catch {
      return
    }
    if (this.parado || this.reconciliando > 0) return
    const s = this.loja.getState()
    if (!s.clashTriageHydrated || s.clashResults !== r) return
    const pares = r.map((x) => ({ key: clashKey(x), guids: guids[clashKey(x)] ?? [null, null] as const, kind: x.kind }))
    const triagem = this.fundirPares(s.clashTriage, pares)
    if (triagem !== s.clashTriage) {
      this.escreverNoStore(triagem, s.clashClassificacao, null, s.clashHistorico, s.clashProjectoBcf)
    }
  }

  // --- Estado para a interface -----------------------------------------------------

  private publicar(): void {
    if (!this.o.aoEstado) return
    this.o.aoEstado(this.fotografia())
  }

  fotografia(): EstadoDoMotor {
    const s = this.loja.getState()
    const indice = indiceDeIdentidades(s.clashTriage, this.chaves)
    let semCorrespondencia = 0
    for (const it of Object.values(this.espelho)) {
      if (it.tipo !== 'conflito' || indice.has(it.chave)) continue
      if (Object.values(it.campos).some((v) => v !== null && v !== undefined)) semCorrespondencia += 1
    }
    let estado: EstadoDaLigacao
    if (this.parado) estado = 'parada'
    else if (this.semRede) estado = 'sem-ligacao'
    else if (!this.lidoUmaVez) estado = 'a-sincronizar'
    else if (this.ligado && !this.canalRecusado) estado = 'ao-vivo'
    else estado = 'sem-canal'
    return {
      estado,
      pendentes: pendentesNaFila(this.fila),
      presentes: this.presentes,
      eu: this.euNoCanal,
      semCorrespondencia,
      semIdentidade: [...this.semIdentidade].filter((k) => s.clashTriage[k]).length,
      erro: this.erro,
    }
  }

  /** Para os testes: a fila e o espelho. */
  inspeccionar(): { fila: Fila; espelho: Espelho; lido: number; chaves: Record<string, string> } {
    return { fila: this.fila, espelho: this.espelho, lido: this.lido, chaves: this.chaves }
  }
}
